import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useListState } from "@mantine/hooks";

import * as Sentry from "@sentry/react";

import { CheckboxItem } from "../types/export";

interface UseCheckboxListOptions {
  /** Callback to fetch all item keys when "Select All" is clicked. Used for paginated data. */
  onSelectAll?: () => Promise<string[]>;
}

export function useCheckboxList<T extends CheckboxItem>(
  initialItems: T[],
  opened: boolean,
  options?: UseCheckboxListOptions,
) {
  const [items, handler] = useListState<T>(initialItems);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  // Tracks if user was in "select all" mode but unchecked some items
  // Used to show indeterminate state across page changes
  const [hasExcludedItems, setHasExcludedItems] = useState(false);
  // Stores the total count of items when "Select All" was clicked
  // Used to detect when all items are re-selected after exclusions
  const [selectAllTotalCount, setSelectAllTotalCount] = useState<number | null>(null);

  // Use ref to access isAllSelected without triggering the merge effect when it changes
  // This prevents the flicker when unchecking an item after "Select All"
  const isAllSelectedRef = useRef(isAllSelected);
  isAllSelectedRef.current = isAllSelected;

  useEffect(() => {
    if (!opened) {
      setSelectedIds(new Set());
      setIsAllSelected(false);
      setHasExcludedItems(false);
      setSelectAllTotalCount(null);
      return;
    }

    // Merge new items with persisted selections or auto-select if isAllSelected is true
    // Uses ref to read isAllSelected without depending on it, so toggling off doesn't re-merge
    const mergedItems = initialItems.map((item) => ({
      ...item,
      checked: isAllSelectedRef.current || selectedIds.has(item.key) ? true : item.checked,
    }));
    handler.setState(mergedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initialItems]);

  // Sync selectedIds whenever items change
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      items.forEach((item) => {
        if (item.checked) {
          next.add(item.key);
        } else {
          next.delete(item.key);
        }
      });
      return next;
    });
  }, [items]);

  // Detect when all items are re-selected after exclusions
  useEffect(() => {
    if (
      hasExcludedItems &&
      selectAllTotalCount !== null &&
      selectedIds.size === selectAllTotalCount
    ) {
      setHasExcludedItems(false);
      setIsAllSelected(true);
    }
  }, [selectedIds.size, hasExcludedItems, selectAllTotalCount]);

  const allChecked = useMemo(
    // All checked only if isAllSelected is true (all items across all pages selected)
    // OR if all visible items are checked AND we haven't excluded any items from a previous "select all"
    () =>
      isAllSelected ||
      (!hasExcludedItems && items.length > 0 && items.every((item) => item.checked)),
    [items, isAllSelected, hasExcludedItems],
  );

  const someChecked = useMemo(
    // Show indeterminate when some items are selected but not all
    () => selectedIds.size > 0 && !allChecked,
    [selectedIds.size, allChecked],
  );

  const toggleAll = useCallback(async () => {
    if (allChecked || hasExcludedItems) {
      // Deselect all (also handles case where user had excluded items)
      setIsAllSelected(false);
      setHasExcludedItems(false);
      setSelectAllTotalCount(null);
      setSelectedIds(new Set());
      handler.setState((current) => current.map((item) => ({ ...item, checked: false })));
    } else {
      // Select all
      if (options?.onSelectAll) {
        try {
          const allIds = await options.onSelectAll();

          if (!allIds) {
            return;
          }

          setSelectAllTotalCount(allIds.length);
          setSelectedIds(new Set(allIds));
          setIsAllSelected(true);
          handler.setState((current) => current.map((item) => ({ ...item, checked: true })));
        } catch (error) {
          Sentry.captureException(error);
          return;
        }
      } else {
        // No onSelectAll callback - select visible items and update selectedIds immediately
        const visibleIds = items.map((item) => item.key);
        setSelectedIds(new Set(visibleIds));
        handler.setState((current) => current.map((item) => ({ ...item, checked: true })));
      }
    }
  }, [allChecked, hasExcludedItems, handler, options, items]);

  // When an individual item is changed, update selectedIds immediately
  const onItemChange = useCallback(
    (index: number, checked: boolean) => {
      const itemKey = items[index]?.key;

      if (!checked && isAllSelected) {
        // Unchecking while in "select all" mode - switch to partial selection
        if (itemKey) {
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(itemKey);
            return next;
          });
        }
        setIsAllSelected(false);
        setHasExcludedItems(true);
      } else if (itemKey) {
        // Update selectedIds immediately for any check/uncheck
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (checked) {
            next.add(itemKey);
          } else {
            next.delete(itemKey);
          }
          return next;
        });
      }

      handler.setItemProp(index, "checked", checked);
    },
    [handler, isAllSelected, items],
  );

  const getSelectedIds = (): string[] => {
    return Array.from(selectedIds);
  };

  return {
    items,
    handler,
    allChecked,
    someChecked,
    toggleAll,
    getSelectedIds,
    selectedCount: selectedIds.size,
    isAllSelected,
    onItemChange,
  };
}
